import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Star, MapPin, ShieldCheck } from "lucide-react";

export default function Directory() {
  const [catalog, setCatalog] = useState({ sports: [], config: {} });
  const [filters, setFilters] = useState({ sport: "all", role: "all", city: "all", q: "" });
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = {};
    if (filters.sport !== "all") params.sport = filters.sport;
    if (filters.role !== "all") params.role = filters.role;
    if (filters.city !== "all") params.city = filters.city;
    if (filters.q) params.q = filters.q;
    const t = setTimeout(() => api.get("/professionals", { params }).then((r) => setData(r.data)).catch(() => setData(false)), 250);
    return () => clearTimeout(t);
  }, [filters]);

  return (
    <div className="fade-up">
      <PageHeader overline="The sports workforce graph" title="Workforce Directory"
        subtitle="Search verified professionals across every sport, role and location." testid="directory-page" />

      <div className="krida-card p-4 mb-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="directory-search" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Search by name…" className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-black/40 border border-white/10 outline-none text-sm focus:border-krida-crimson/60" />
        </div>
        <Sel value={filters.sport} onChange={(v) => setFilters((f) => ({ ...f, sport: v }))} label="Sport" options={["all", ...catalog.sports.map((s) => s.name)]} testid="dir-sport" />
        <Sel value={filters.role} onChange={(v) => setFilters((f) => ({ ...f, role: v }))} label="Role" options={["all", ...(catalog.config?.professional_roles || [])]} testid="dir-role" />
        <Sel value={filters.city} onChange={(v) => setFilters((f) => ({ ...f, city: v }))} label="City" options={["all", "Chennai", "Bengaluru", "Mumbai", "Hyderabad", "Delhi", "Pune", "Kochi"]} testid="dir-city" />
      </div>

      {data === null ? <Loader /> : !data ? <Empty /> : (
        <>
          <div className="text-sm text-slate-500 mb-4 font-mono">{data.total} professionals</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.items.map((p) => (
              <div key={p.id} className="krida-card p-4" data-testid={`dir-pro-${p.id}`}>
                <div className="flex items-center gap-3">
                  <img src={p.photo} alt={p.name} className="w-12 h-12 rounded-xl object-cover border border-white/10" />
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-1.5">{p.name}{p.verification_status === "Verified" && <ShieldCheck className="w-3.5 h-3.5 text-krida-emerald flex-shrink-0" />}</div>
                    <div className="text-xs text-slate-500 font-mono">{p.krida_id}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3"><Tag tone="crimson">{p.primary_role}</Tag><Tag tone="cobalt">{p.primary_sport}</Tag><Tag>{p.primary_level}</Tag></div>
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-krida-amber" /> {p.rating}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.location.city}</span>
                  <span>{p.experience_years}y exp</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-40 bg-black/40 border-white/10 rounded-xl text-sm"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="bg-krida-card border-white/10 text-slate-100 max-h-72">{options.map((o) => <SelectItem key={o} value={o}>{o === "all" ? `All ${label}s` : o}</SelectItem>)}</SelectContent>
    </Select>
  );
}
